import User from "../services/User";
import userActions from "./user";
import setRegionPreferenceCookie from "../utils/setRegionPreferenceCookie";
import assign from "lodash.assign";
import { LOCATION_CHANGE } from "react-router-redux";
import { ABBA_CONFIGS, REFERRAL_SHARING_PREFERENCES } from "common/constants/Constants";
import { setErrorInErrors, clearErrorFromErrors } from "utils/errorsUtil";
import REDUX_ERROR_TYPES from "constants/ReduxErrorTypes";

const SUCCESS_MESSAGE_TYPE = "success";
const ERROR_MESSAGE_TYPE = "error";
const DATA_SAVED = "Saved";

// Actions (must be name-spaced for reducer)

export const ACCOUNT_SET_ELEMENT_TO_FOCUS = "ACCOUNT_SET_ELEMENT_TO_FOCUS";
export const ACCOUNT_SET_EMAIL_EDITABLE = "ACCOUNT_SET_EMAIL_EDITABLE";
export const ACCOUNT_SET_PASSWORD_EDITABLE = "ACCOUNT_SET_PASSWORD_EDITABLE";
export const ACCOUNT_RESEND_CONFIRMATION_EMAIL_SUCCESS = "ACCOUNT_RESEND_CONFIRMATION_EMAIL_SUCCESS";
export const ACCOUNT_UPDATE_REGION_SUCCESS = "ACCOUNT_UPDATE_REGION_SUCCESS";
export const ACCOUNT_UPDATE_INDUSTRY_SUCCESS = "ACCOUNT_UPDATE_INDUSTRY_SUCCESS";
export const ACCOUNT_UPDATE_JOB_TITLE_SUCCESS = "ACCOUNT_UPDATE_JOB_TITLE_SUCCESS";
export const ACCOUNT_UPDATE_SENIORITY_SUCCESS = "ACCOUNT_UPDATE_SENIORITY_SUCCESS";
export const ACCOUNT_DEACTIVATE_ACCOUNT_SUCCESS = "ACCOUNT_DEACTIVATE_ACCOUNT_SUCCESS";
export const ACCOUNT_BW_ACCOUNT_ID_CHANGE = "ACCOUNT_BW_ACCOUNT_ID_CHANGE";
export const ACCOUNT_CLEAR_MARKETING_MESSAGE = "ACCOUNT_CLEAR_MARKETING_MESSAGE";
export const ACCOUNT_CLEAR_REFERRALS_MESSAGE = "ACCOUNT_CLEAR_REFERRALS_MESSAGE";
export const ACCOUNT_SHOW_MARKETING_MESSAGE = "ACCOUNT_SHOW_MARKETING_MESSAGE";
export const ACCOUNT_SHOW_REFERRALS_MESSAGE = "ACCOUNT_SHOW_REFERRALS_MESSAGE";
export const ACCOUNT_SHOW_ERROR = "ACCOUNT_SHOW_ERROR";
export const ACCOUNT_CLEAR_ERROR = "ACCOUNT_CLEAR_ERROR";

// Reducer

export function accountReducer(state = { errors: {} }, action = {}) {
    switch (action.type) {
        case LOCATION_CHANGE:
            return { errors: {} };
        case ACCOUNT_SET_ELEMENT_TO_FOCUS:
            return assign({}, state, {
                elementToFocus: action.elementToFocus
            });
        case ACCOUNT_SET_EMAIL_EDITABLE:
            return assign({}, state, {
                editEmailEnabled: action.editEmailEnabled
            });
        case ACCOUNT_RESEND_CONFIRMATION_EMAIL_SUCCESS:
            return assign({}, state, {
                resendConfirmationEmailSuccessMessage: action.successMessage
            });
        case ACCOUNT_SET_PASSWORD_EDITABLE:
            return assign({}, state, {
                editPasswordEnabled: action.editPasswordEnabled
            });
        case ACCOUNT_UPDATE_REGION_SUCCESS:
            return assign({}, state, {
                updateRegionSuccessMessage: action.successMessage
            });
        case ACCOUNT_UPDATE_INDUSTRY_SUCCESS:
            return assign({}, state, {
                updateIndustrySuccessMessage: action.successMessage
            });
        case ACCOUNT_UPDATE_JOB_TITLE_SUCCESS:
            return assign({}, state, {
                updateJobTitleSuccessMessage: action.successMessage
            });
        case ACCOUNT_UPDATE_SENIORITY_SUCCESS:
            return assign({}, state, {
                updateSenioritySuccessMessage: action.successMessage
            });
        case ACCOUNT_BW_ACCOUNT_ID_CHANGE:
            return assign({}, state, {
                subscriptionId: action.subscriptionId
            });
        case ACCOUNT_CLEAR_MARKETING_MESSAGE:
            return assign({}, state, {
                marketingMessage: null,
                marketingMessageType: null
            });
        case ACCOUNT_CLEAR_REFERRALS_MESSAGE:
            return assign({}, state, {
                referralsMessage: null,
                referralsMessageType: null
            });
        case ACCOUNT_SHOW_MARKETING_MESSAGE:
            return assign({}, state, {
                marketingMessage: action.message,
                marketingMessageType: action.messageType
            });
        case ACCOUNT_SHOW_REFERRALS_MESSAGE:
            return assign({}, state, {
                referralsMessage: action.message,
                referralsMessageType: action.messageType
            });
        case ACCOUNT_SHOW_ERROR:
            return assign({}, state, {
                errors: setErrorInErrors(state.errors, action.error)
            });
        case ACCOUNT_CLEAR_ERROR:
            return assign({}, state, {
                errors: clearErrorFromErrors(state.errors, action.errorType)
            });
        default:
            return state;
    }
}

// Action Creators

export function showError(error) {
    return {
        type: ACCOUNT_SHOW_ERROR,
        error
    };
}

export function clearError(errorType) {
    return {
        type: ACCOUNT_CLEAR_ERROR,
        errorType
    };
}

export function setElementToFocus(elementToFocus) {
    return {
        type: ACCOUNT_SET_ELEMENT_TO_FOCUS,
        elementToFocus
    };
}

export function setEmailEditable(editEmailEnabled) {
    return {
        type: ACCOUNT_SET_EMAIL_EDITABLE,
        editEmailEnabled
    };
}

export function resendConfirmationEmailSuccess(successMessage) {
    return {
        type: ACCOUNT_RESEND_CONFIRMATION_EMAIL_SUCCESS,
        successMessage
    };
}

export function setPasswordEditable(editPasswordEnabled) {
    return {
        type: ACCOUNT_SET_PASSWORD_EDITABLE,
        editPasswordEnabled
    };
}

export function updateRegionSuccess(successMessage) {
    return {
        type: ACCOUNT_UPDATE_REGION_SUCCESS,
        successMessage
    };
}

export function updateIndustrySuccess(successMessage) {
    return {
        type: ACCOUNT_UPDATE_INDUSTRY_SUCCESS,
        successMessage
    };
}

export function updateJobTitleSuccess(successMessage) {
    return {
        type: ACCOUNT_UPDATE_JOB_TITLE_SUCCESS,
        successMessage
    };
}

export function updateSenioritySuccess(successMessage) {
    return {
        type: ACCOUNT_UPDATE_SENIORITY_SUCCESS,
        successMessage
    };
}

export function showMarketingMessage(messageType, message) {
    return {
        type: ACCOUNT_SHOW_MARKETING_MESSAGE,
        messageType,
        message
    };
}

export function showReferralsMessage(messageType, message) {
    return {
        type: ACCOUNT_SHOW_REFERRALS_MESSAGE,
        messageType,
        message
    };
}

export function accountIdChange(subscriptionId) {
    return {
        type: ACCOUNT_BW_ACCOUNT_ID_CHANGE,
        subscriptionId
    };
}

export function clearMarketingMessage() {
    return {
        type: ACCOUNT_CLEAR_MARKETING_MESSAGE
    };
}

export function clearReferralsMessage() {
    return {
        type: ACCOUNT_CLEAR_REFERRALS_MESSAGE
    };
}

export function clearConfirmationEmailMessage() {
    return (dispatch) => {
        dispatch(resendConfirmationEmailSuccess(null));
        dispatch(clearError(REDUX_ERROR_TYPES.RESEND_CONFIRMATION_EMAIL));
    };
}

export function editPassword() {
    return (dispatch) => {
        dispatch(setPasswordEditable(true));
        dispatch(setElementToFocus("oldPasswordInput"));
    };
}

export function updatePasswordSuccess() {
    return (dispatch) => {
        dispatch(setPasswordEditable(false));
        dispatch(clearError(REDUX_ERROR_TYPES.PASSWORD));
    };
}

export function updatePasswordFailed(message) {
    return (dispatch) => {
        dispatch(setPasswordEditable(true));
        dispatch(showError({ type: REDUX_ERROR_TYPES.PASSWORD, message }));
    };
}

export function cancelEditPassword() {
    return (dispatch) => {
        dispatch(setPasswordEditable(false));
        dispatch(clearError(REDUX_ERROR_TYPES.PASSWORD));
    };
}

export function editEmail() {
    return (dispatch) => {
        dispatch(setEmailEditable(true));
        dispatch(setElementToFocus("emailInput"));
    };
}

export function updateEmailSuccess() {
    return (dispatch) => {
        dispatch(setEmailEditable(false));
        dispatch(clearError(REDUX_ERROR_TYPES.EMAIL));
    };
}

export function updateEmailFailed(message) {
    return (dispatch) => {
        dispatch(setEmailEditable(true));
        dispatch(showError({ type: REDUX_ERROR_TYPES.EMAIL, message }));
    };
}

export function cancelEditEmail() {
    return (dispatch) => {
        dispatch(setEmailEditable(false));
        dispatch(clearError(REDUX_ERROR_TYPES.EMAIL));
    };
}

export function startUpdatingEmail(email) {
    return (dispatch) => {
        return User.updateEmail(email)
            .then((user) => {
                dispatch(userActions.updateUserStore(user));
                dispatch(updateEmailSuccess());
            })
            .catch((error) => {
                dispatch(updateEmailFailed(error.errorMessage));

                throw error;
            });
    };
}

export function resendConfirmationEmail() {
    return (dispatch) => {
        return User.resendConfirmationEmail()
            .then(({ successMessage }) => {
                dispatch(resendConfirmationEmailSuccess(successMessage));
            })
            .catch((error) => {
                dispatch(showError({ type: REDUX_ERROR_TYPES.RESEND_CONFIRMATION_EMAIL, message: error.errorMessage }));

                throw error;
            });
    };
}

export function resendBulkSubscriptionConfirmationEmail(email, resetPassword) {
    return (dispatch) => {
        return User.resendBulkSubscriptionConfirmationEmail(email, resetPassword)
            .then(({ successMessage }) => {
                dispatch(resendConfirmationEmailSuccess(successMessage));
            })
            .catch((error) => {
                dispatch(showError({ type: REDUX_ERROR_TYPES.RESEND_CONFIRMATION_EMAIL, message: error.errorMessage }));

                throw error;
            });
    };
}

export function startUpdatingPassword(oldPassword, newPassword) {
    return (dispatch) => {
        return User.updatePassword(oldPassword, newPassword)
            .then(() => {
                dispatch(updatePasswordSuccess());
            })
            .catch((error) => {
                dispatch(updatePasswordFailed(error.errorMessage));

                throw error;
            });
    };
}

export function startUpdatingRegion(region) {
    return (dispatch) => {
        return User.updateRegion(region)
            .then((success) => {
                setRegionPreferenceCookie(region);
                dispatch(updateRegionSuccess(success.successMessage));
            })
            .catch((error) => {
                dispatch(showError({ type: REDUX_ERROR_TYPES.REGION, message: error.errorMessage }));

                throw error;
            });
    };
}

export function startUpdatingIndustry(industry) {
    return (dispatch) => {
        return User.updateProfession({ industry })
            .then((success) => {
                dispatch(updateIndustrySuccess(success.successMessage));
            })
            .catch((error) => {
                dispatch(showError({ type: REDUX_ERROR_TYPES.PROFESSION, message: error.errorMessage }));
            });
    };
}

export function startUpdatingJobTitle(jobTitle) {
    return (dispatch) => {
        return User.updateProfession({ jobTitle })
            .then((success) => {
                dispatch(updateJobTitleSuccess(success.successMessage));
            })
            .catch((error) => {
                dispatch(showError({ type: REDUX_ERROR_TYPES.PROFESSION, message: error.errorMessage }));
            });
    };
}

export function startUpdatingSeniority(seniority) {
    return (dispatch) => {
        return User.updateProfession({ seniority })
            .then((success) => {
                dispatch(updateSenioritySuccess(success.successMessage));
            })
            .catch((error) => {
                dispatch(showError({ type: REDUX_ERROR_TYPES.PROFESSION, message: error.errorMessage }));
            });
    };
}

export function startUpdatingMarketingOptions(marketingOptionId, status) {
    return (dispatch) => {
        dispatch(clearMarketingMessage());
        dispatch(userActions.updateMarketingOption(marketingOptionId, status));

        return User.updateMarketingOptions(marketingOptionId, status)
            .then((user) => {
                dispatch(showMarketingMessage(SUCCESS_MESSAGE_TYPE, DATA_SAVED));
                dispatch(userActions.updateUserStore(user));
            })
            .catch((error) => {
                dispatch(showMarketingMessage(ERROR_MESSAGE_TYPE, error.errorMessage));

                throw error;
            });
    };
}

export function startUpdatingReferralSharingPreference(status) {
    return (dispatch, getState) => {
        dispatch(clearReferralsMessage());

        const {
            abba: {
                config: {
                    [ABBA_CONFIGS.REFERRALS_OPT_IN]: { ratePlanId } = { ratePlanId: "" }
                }
            }
        } = getState();

        if (!ratePlanId) {
            return Promise.resolve();
        }

        // Update the checkbox on UI first as api call to update backend takes longer
        dispatch(userActions.updateReferralSharingPreference(status));

        return User.updateReferralSharingPreference(status, ratePlanId)
            .then((user) => {
                dispatch(showReferralsMessage(SUCCESS_MESSAGE_TYPE, DATA_SAVED));
                dispatch(userActions.updateUserStore(user));
            })
            .catch((error) => {
                // Revert the checkbox on UI as api call to update backend falied
                dispatch(userActions.updateReferralSharingPreference(status === REFERRAL_SHARING_PREFERENCES.OPT_IN
                    ? REFERRAL_SHARING_PREFERENCES.OPT_OUT : REFERRAL_SHARING_PREFERENCES.OPT_IN));
                dispatch(showReferralsMessage(ERROR_MESSAGE_TYPE, error.errorMessage));

                throw error;
            });
    };
}

export function startDeactivatingAccount() {
    return (dispatch) => {
        return User.deactivateAccount()
            .catch((error) => {
                dispatch(showError({ type: REDUX_ERROR_TYPES.DEACTIVATE_ACCOUNT, message: error.errorMessage }));

                throw error;
            });
    };
}

export default {
    accountReducer,
    editEmail,
    editPassword,
    cancelEditEmail,
    cancelEditPassword,
    updateEmailFailed,
    resendConfirmationEmail,
    updatePasswordSuccess,
    updatePasswordFailed,
    startUpdatingEmail,
    startUpdatingPassword,
    startUpdatingRegion,
    startUpdatingIndustry,
    startDeactivatingAccount,
    startUpdatingMarketingOptions,
    accountIdChange,
    showError,
    clearError,
    startUpdatingReferralSharingPreference
};
